import { useInfiniteQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { stringify } from 'query-string';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import Table from '../components/Table';

import { useAuth } from '@dashboard/context/AuthContext/AuthContext';

import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';

import { formatPrice } from '@dashboard/services/formatting';
import getCheckoutUrl from '@dashboard/services/getCheckoutUrl';
import { fetchInfinite } from '@dashboard/services/request';

import BottomScrollListener from '@dashboard/components/BottomScrollListener';
import Button from '@dashboard/components/Button';
import { Select } from '@dashboard/components/Form';
import FormElement from '@dashboard/components/Form/components/FormElement/FormElement';
import Label from '@dashboard/components/Form/components/Label';
import TextInput from '@dashboard/components/Form/components/TextInput';
import Link from '@dashboard/components/Link';
import Lozenge from '@dashboard/components/Lozenge';
import Spinner from '@dashboard/components/Spinner';
import StatusBadge from '@dashboard/components/StatusBadge';

const Pages = () => {
  const { token } = useAuth();
  const history = useHistory();

  const { search, searchInput, setSearchInput } = useSearchInput();
  const query = useUrlQuery();
  const [sortBy, setSortBy] = useState(query.sortBy || 'createdAt');
  const [sortDirection, setSortDirection] = useState(query.sortDirection || -1);
  const [pageType, setPageType] = useState(query.pageType || '');
  const [usageType, setUsageType] = useState(query.usageType || '');
  const [hostedLayoutType, setHostedLayoutType] = useState(query.hostedLayoutType || '');
  const [siteLayoutType, setSiteLayoutType] = useState(query.siteLayoutType || '');
  const [subscriptionStatus, setSubscriptionStatus] = useState(query.subscriptionStatus || '');

  useEffect(() => {
    history.replace({
      search: stringify({
        sortBy,
        sortDirection,
        search,
        ...(pageType && { pageType }),
        ...(usageType && { usageType }),
        ...(hostedLayoutType && { hostedLayoutType }),
        ...(siteLayoutType && { siteLayoutType }),
        ...(subscriptionStatus && { subscriptionStatus }),
      }),
    });
  }, [
    sortBy,
    sortDirection,
    search,
    pageType,
    usageType,
    hostedLayoutType,
    siteLayoutType,
    subscriptionStatus,
  ]);

  useEffect(() => {
    if (usageType === 'hosted') setSiteLayoutType('');
    if (usageType === 'site') setHostedLayoutType('');
  }, [usageType]);

  const { data, fetchNextPage, isLoading, isFetchingNextPage } = useInfiniteQuery({
    queryKey: [
      'payments',
      sortBy,
      sortDirection,
      search,
      pageType,
      usageType,
      hostedLayoutType,
      siteLayoutType,
      subscriptionStatus,
    ],
    queryFn: async ({ pageParam }) =>
      fetchInfinite(
        `/admin/pages?${stringify({
          sort: JSON.stringify({
            [sortBy]: sortDirection,
          }),
          search,
          ...(pageType && { type: pageType }),
          ...(usageType && { usageType }),
          ...(hostedLayoutType && { hostedLayoutType }),
          ...(siteLayoutType && { siteLayoutType }),
          ...(subscriptionStatus && {
            'seller.subscriptionStatus': 'active',
          }),
          limit: 20,
          skip: pageParam * 20,
        })}`,
        token,
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.has_more) return undefined;

      return allPages.length;
    },
  });

  function onSort(newSortBy) {
    const oppositeSortDirection = sortDirection * -1;

    setSortDirection(sortBy === newSortBy ? oppositeSortDirection : -1);
    setSortBy(newSortBy);
  }

  const total = data?.pages[0]?.total;

  return (
    <div className="">
      <div className="flex items-end justify-end gap-x-2">
        <div className="mr-auto">{total} pages</div>
        {/* <FormElement className="!mb-0">
          <Label htmlFor="checkoutType">Checkout type</Label>
          <Select
            name="checkoutType"
            label="Checkout type"
            onChange={e => setCheckoutType(e.target.value)}
            className="w-32"
          >
            <option value="">All</option>

            <option value="charge" selected={checkoutType === 'charge'}>
              One time payment
            </option>
            <option
              value="subscription"
              selected={checkoutType === 'subscription'}
            >
              Subscription
            </option>
          </Select>
        </FormElement> */}

        <FormElement className="!mb-0">
          <Label htmlFor="subscriptionStatus">Merchant subscription status</Label>
          <Select
            name="subscriptionStatus"
            label="Subscription status"
            onChange={e => setSubscriptionStatus(e.target.value)}
            className="!w-40"
            value={subscriptionStatus}
          >
            <option value="">All</option>
            <option value="trialing">Trialing</option>
            <option value="active">Active</option>
            <option value="trial_expired">Trial expired</option>
            <option value="canceled">Canceled</option>
            <option value="legacy">Legacy</option>
          </Select>
        </FormElement>

        <FormElement className="!mb-0">
          <Label htmlFor="pageType">Page type</Label>
          <Select
            name="pageType"
            label="Page type"
            onChange={e => setPageType(e.target.value)}
            className="w-32"
          >
            <option value="">All</option>
            <option value="checkout" selected={pageType === 'checkout'}>
              Checkout
            </option>
            <option value="event" selected={pageType === 'event'}>
              Event
            </option>
            <option value="form" selected={pageType === 'form'}>
              Form
            </option>
          </Select>
        </FormElement>

        <FormElement className="!mb-0">
          <Label htmlFor="usageType">Usage type</Label>
          <Select
            name="usageType"
            label="Usage type"
            onChange={e => setUsageType(e.target.value)}
            className="w-32"
          >
            <option value="">All</option>
            <option value="hosted" selected={usageType === 'hosted'}>
              Hosted
            </option>
            <option value="site" selected={usageType === 'site'}>
              Site
            </option>
          </Select>
        </FormElement>

        {usageType === 'hosted' && (
          <FormElement className="!mb-0">
            <Label htmlFor="hostedLayoutType">Hosted layout type</Label>
            <Select
              name="hostedLayoutType"
              label="Hosted layout type"
              onChange={e => setHostedLayoutType(e.target.value)}
              className="w-32"
            >
              <option value="">All</option>
              <option value="double" selected={hostedLayoutType === 'double'}>
                Double
              </option>
              <option value="center" selected={hostedLayoutType === 'center'}>
                Center
              </option>
              <option value="split" selected={hostedLayoutType === 'split'}>
                Split
              </option>
            </Select>
          </FormElement>
        )}

        {usageType === 'site' && (
          <FormElement className="!mb-0">
            <Label htmlFor="siteLayoutType">Site layout type</Label>
            <Select
              name="siteLayoutType"
              label="Site layout type"
              onChange={e => setSiteLayoutType(e.target.value)}
              className="w-32"
            >
              <option value="">All</option>
              <option value="embed" selected={siteLayoutType === 'embed'}>
                Embed
              </option>

              <option value="popup" selected={siteLayoutType === 'popup'}>
                Pop-up
              </option>
            </Select>
          </FormElement>
        )}

        <FormElement className="!mb-0">
          <Label htmlFor="search">Search</Label>
          <div className="w-64">
            <TextInput
              border
              name="search"
              onChange={e => setSearchInput(e.target.value)}
              placeholder="Search by checkout name, product title"
              value={searchInput}
            />
          </div>
        </FormElement>
      </div>

      <Table.Table>
        <Table.Head>
          <Table.Th onSort={onSort} sortKey="name" sortBy={sortBy} sortDirection={sortDirection}>
            Page
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="product.price"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Price
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="type" sortBy={sortBy} sortDirection={sortDirection}>
            Type
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="usageType"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Usage
          </Table.Th>

          <Table.Th onSort={onSort} sortKey="layout" sortBy={sortBy} sortDirection={sortDirection}>
            Layout
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="seller.displayName"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Merchant
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="product.chargeCount"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Payments
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="createdAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Date created
          </Table.Th>

          <Table.Th
            onSort={onSort}
            sortKey="updatedAt"
            sortBy={sortBy}
            sortDirection={sortDirection}
          >
            Last updated
          </Table.Th>
          <Table.Th></Table.Th>
        </Table.Head>

        {!isLoading && (
          <BottomScrollListener onBottom={fetchNextPage} as="tbody">
            {data.pages.map(page =>
              page.data.map(checkout => (
                <Table.Row key={checkout.id}>
                  <Table.Td className="!max-w-xs">
                    <Link href={`/pages/${checkout._id}/customize`}>{checkout.name}</Link>

                    {page.status === 'archived' && <StatusBadge status={page.status} />}
                  </Table.Td>

                  <Table.Td>
                    {checkout.type === 'checkout' &&
                      checkout.product &&
                      formatPrice({
                        amount: checkout.product.price,
                        currency: checkout.product.currency,
                      })}
                  </Table.Td>

                  <Table.Td>{checkout.type}</Table.Td>

                  <Table.Td>{checkout.usageType}</Table.Td>

                  <Table.Td>
                    {checkout.usageType === 'hosted' && checkout.hostedLayoutType}
                    {checkout.usageType === 'site' && checkout.siteLayoutType}
                  </Table.Td>

                  <Table.Td className="relative !max-w-xs">
                    <Link href={`/admin/merchants/${checkout.seller?._id}`}>
                      {checkout.seller?.displayName || checkout.seller?.name}
                    </Link>

                    {checkout.seller?.disabled && (
                      <Lozenge className="ml-2 absolute right-0 mt-0.5">Disabled</Lozenge>
                    )}
                  </Table.Td>

                  <Table.Td>
                    {checkout.type === 'checkout' &&
                      checkout.product &&
                      checkout.product.chargeCount}

                    {checkout.type === 'form' && checkout.submissionCount}
                  </Table.Td>

                  <Table.Td>
                    {checkout.createdAt &&
                      format(new Date(checkout.createdAt), 'dd MMM yyyy HH:mm')}
                  </Table.Td>
                  <Table.Td>
                    {checkout.updatedAt &&
                      format(new Date(checkout.updatedAt), 'dd MMM yyyy HH:mm')}
                  </Table.Td>
                  <Table.Td>
                    {checkout.seller && (
                      <Button
                        to={getCheckoutUrl(checkout, checkout.seller)}
                        appearance="light"
                        target="_blank"
                        size="small"
                      >
                        View checkout
                      </Button>
                    )}
                  </Table.Td>
                </Table.Row>
              )),
            )}
          </BottomScrollListener>
        )}
      </Table.Table>

      {(isFetchingNextPage || isLoading) && <Spinner />}
    </div>
  );
};

export default Pages;
